/**
 * AutoSave — periodic crash-recovery snapshots to IndexedDB.
 *
 * Saves the full map state (TMJ) every N seconds when the editor is dirty.
 * On next load, checks for a recovery snapshot and offers to restore it.
 *
 * Storage: IndexedDB database 'tileforge-autosave', single object store.
 * Only keeps the latest snapshot (overwrites on each save).
 */

import { exportTMJ } from '../core/TMJExporter.js';

const DB_NAME = 'tileforge-autosave';
const DB_VERSION = 1;
const LS_KEY = 'tileforge-autosave-emergency';
const STORE_NAME = 'snapshots';

// Per-tab session ID prevents multi-tab overwrite collisions.
// Each tab saves to its own key; recovery shows the most recent.
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Default auto-save interval: 30 seconds. */
const DEFAULT_INTERVAL_MS = 30 * 1000;

export class AutoSave {
  /**
   * @param {Object} state — EditorState instance
   * @param {Object} [options]
   * @param {number} [options.intervalMs] — save interval in ms (default 30s)
   */
  constructor(state, options = {}) {
    this._state = state;
    this._intervalMs = options.intervalMs || DEFAULT_INTERVAL_MS;
    this._timer = null;
    this._db = null;
    this._saving = false;
    this._lastSavedAt = null;
  }

  /** Start periodic auto-saving. Call once after editor is initialized. */
  async start() {
    await this._openDB();
    this._timer = setInterval(() => this._tick(), this._intervalMs);

    // Last-chance snapshot on beforeunload.
    // IDB writes are async and may not complete before page tears down,
    // so we also write a compact snapshot to localStorage (synchronous).
    window.addEventListener('beforeunload', () => {
      this._saveNow(); // Best-effort IDB write (may not complete)
      this._saveToLocalStorage(); // Synchronous fallback
    });
  }

  /** Stop auto-saving and close DB. */
  stop() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  /** Find the most recent snapshot across all tab keys. */
  async _getMostRecentSnapshot() {
    if (!this._db) return null;
    const all = await this._getAll();
    if (all.length === 0) return null;
    // Return the newest by timestamp
    return all.reduce((best, s) => (!best || s.timestamp > best.timestamp) ? s : best, null);
  }

  /** Check if a recovery snapshot exists. Call before start(). */
  async hasRecovery() {
    await this._openDB();
    const snapshot = await this._getMostRecentSnapshot();
    if (snapshot) return true;
    return this._getLocalStorageRecovery() != null;
  }

  /** Get the recovery snapshot metadata (timestamp, map size). */
  async getRecoveryInfo() {
    let snapshot = await this._getMostRecentSnapshot();
    if (!snapshot) snapshot = this._getLocalStorageRecovery();
    if (!snapshot) return null;
    return {
      timestamp: snapshot.timestamp,
      age: Date.now() - snapshot.timestamp,
      mapWidth: snapshot.tmj?.width || 0,
      mapHeight: snapshot.tmj?.height || 0,
      layerCount: snapshot.tmj?.layers?.length || 0,
      fileName: snapshot.fileName || 'Untitled',
    };
  }

  /** Get the full recovery TMJ data. */
  async getRecoveryData() {
    let snapshot = await this._getMostRecentSnapshot();
    if (!snapshot) snapshot = this._getLocalStorageRecovery();
    return snapshot?.tmj || null;
  }

  /** Clear all recovery snapshots (all tabs + localStorage). */
  async clearRecovery() {
    this._clearLocalStorageRecovery();
    await this._openDB();
    if (!this._db) return;
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear(); // Clear all tab snapshots
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Get the timestamp of the last auto-save. */
  get lastSavedAt() { return this._lastSavedAt; }

  // ─── Internal ──────────────────────────────────────────────────

  _tick() {
    // Only save if the editor has unsaved changes
    if (!this._state.get('dirty')) return;
    // Don't overlap saves
    if (this._saving) return;
    this._saveNow();
  }

  async _saveNow() {
    if (this._saving) return;
    // Don't save if there are no layers (empty/uninitialized state)
    const layers = this._state.get('layers');
    if (!layers || layers.length === 0) return;

    this._saving = true;
    try {
      const tmj = exportTMJ(this._state);
      const snapshot = {
        id: TAB_ID,
        tmj,
        timestamp: Date.now(),
        fileName: this._state.get('fileName') || 'Untitled',
      };
      await this._put(snapshot);
      this._lastSavedAt = snapshot.timestamp;
    } catch (err) {
      // Auto-save is best-effort — don't crash the editor
      console.warn('[AutoSave] Snapshot failed:', err.message);
    } finally {
      this._saving = false;
    }
  }

  /** Synchronous localStorage fallback for beforeunload. */
  _saveToLocalStorage() {
    try {
      const layers = this._state.get('layers');
      if (!layers || layers.length === 0 || !this._state.get('dirty')) return;
      const tmj = exportTMJ(this._state);
      const snapshot = JSON.stringify({
        tmj,
        timestamp: Date.now(),
        fileName: this._state.get('fileName') || 'Untitled',
      });
      localStorage.setItem(LS_KEY, snapshot);
    } catch {
      // localStorage may be full or unavailable — silently skip
    }
  }

  /** Check for localStorage emergency snapshot (call during recovery). */
  _getLocalStorageRecovery() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  _clearLocalStorageRecovery() {
    try { localStorage.removeItem(LS_KEY); } catch {}
  }

  async _openDB() {
    if (this._db) return;
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
      req.onsuccess = () => { this._db = req.result; resolve(); };
      req.onerror = () => reject(req.error);
    });
  }

  _put(record) {
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(record);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  }

  _getAll() {
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  _get(key) {
    return new Promise((resolve, reject) => {
      const tx = this._db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }
}
